import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import SalaIcon from './SalaIcon.jsx';
import MeuPerfilModal from './MeuPerfilModal.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { SALA_ABAS } from '../data/salaAbas.js';

/**
 * SidebarNova — substitui a Sidebar.jsx antiga por inteiro (decisão do
 * Juan, rodada de exploração visual de 29/07/2026: painel flutuante,
 * bem arredondado, vidro fosco, ícone em badge, balão de abas no
 * hover). Mesma interface de props que a antiga (`usuario`, `categorias`,
 * `open`, `onClose`) — o Dashboard.jsx não precisou mudar nada além do
 * import.
 *
 * Duas coisas novas que a antiga não tinha:
 *  1. Modo compacto (só ícone) — persiste por usuário (mesmo padrão de
 *     tema/nav_estilo), via sidebarCompacta/alterarSidebarCompacta do
 *     AuthContext.
 *  2. Balão de abas no hover — passar o mouse sobre uma sala mostra as
 *     abas reais dela (quando cadastradas em data/salaAbas.js) e deixa
 *     entrar direto numa delas via `?aba=slug`; clicar na sala em si
 *     continua abrindo normal, na aba padrão de cada uma.
 *
 * Achado ao migrar: o `usuario?.chave?.nome` que a Sidebar antiga usava
 * pro rótulo de papel não existe mais — o sistema de "chave" foi
 * removido por completo na virada pro modelo de permissões novo (ver
 * ESPECIFICACAO_PERMISSOES_SGQ.md). Troquei pelo isMaster/admDe reais,
 * que é o que o resto do sistema já usa hoje.
 */
export default function SidebarNova({ usuario, categorias, open, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMaster, admDe, sidebarCompacta, alterarSidebarCompacta, fotoUrl, API } = useAuth();
  /* ⚠ "MINHAS SALAS" (26/09/2026, pedido dele: "só a parte de fixar/favoritos,
     sem deixar livre a ordem"). A estrela em cada sala fixa ela no topo; a
     ordem dentro de "Minhas salas" é a MESMA da barra (não se arrasta). Fica
     salvo por pessoa — quem vê uma sala só nem nota. */
  const [fixadas, setFixadas] = useState([]);
  useEffect(() => { API?.get('/me/fixadas').then(r => setFixadas(r.data.salas || [])).catch(() => setFixadas([])); }, []);
  const alternarFixada = (e, numero) => {
    e.stopPropagation(); e.preventDefault();
    const fixar = !fixadas.includes(numero);
    setFixadas(l => (fixar ? [...l, numero] : l.filter(x => x !== numero)));
    API?.put(`/me/fixadas/${numero}`, { fixada: fixar }).catch(() => setFixadas(l => (fixar ? l.filter(x => x !== numero) : [...l, numero])));
  };
  const [modalPerfil, setModalPerfil] = useState(false);
  const [busca, setBusca] = useState('');
  const [flyout, setFlyout] = useState(null); // { numero, nome, abas, top, left }
  const esconderTimeout = useRef(null);

  const iniciais = (nome) =>
    (nome || '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(p => p[0]?.toUpperCase())
      .join('');

  const papel = isMaster ? 'Master' : (admDe?.length > 0 ? 'ADM' : 'Colaborador');

  const irPara = (numero, aba) => {
    navigate(aba ? `/sala/${numero}?aba=${aba}` : `/sala/${numero}`);
    esconderFlyout();
    onClose?.();
  };

  const mostrarFlyout = (sala, alvoEl) => {
    const abas = SALA_ABAS[sala.sala_numero];
    if (!abas || abas.length === 0) return; // sem aba cadastrada — sem balão, clique normal já resolve
    clearTimeout(esconderTimeout.current);
    const r = alvoEl.getBoundingClientRect();
    setFlyout({ numero: sala.sala_numero, nome: sala.nome, abas, top: r.top + r.height / 2, left: r.right + 10 });
  };
  const agendarEsconderFlyout = () => {
    esconderTimeout.current = setTimeout(() => setFlyout(null), 220);
  };
  const esconderFlyout = () => { clearTimeout(esconderTimeout.current); setFlyout(null); };

  const normaliza = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const termo = normaliza(busca.trim());
  const categoriasFiltradas = termo
    ? categorias
        .map(c => ({ ...c, salas: c.salas.filter(s => normaliza(s.nome).includes(termo)) }))
        .filter(c => c.salas.length > 0)
    : categorias;
  /* as fixadas, na ordem em que aparecem na barra */
  const minhas = termo ? [] : categorias.flatMap(c => c.salas).filter(s => fixadas.includes(s.sala_numero));
  const listaCats = minhas.length ? [{ nome: 'Minhas salas', salas: minhas, fixa: true }, ...categoriasFiltradas] : categoriasFiltradas;

  return (
    <>
      <div className={`sbn-overlay ${open ? 'open' : ''}`} onClick={onClose} />
      <aside className={`sbn ${open ? 'open' : ''} ${sidebarCompacta ? 'compacta' : ''}`}>

        <div className="sbn-user">
          <div className="sbn-avatar" style={{ cursor: 'pointer', overflow: 'hidden', padding: 0 }}
            title="Meu perfil" onClick={() => setModalPerfil(true)}>
            {fotoUrl ? <img src={fotoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (iniciais(usuario?.nome) || 'U')}
          </div>
          <div className="sbn-uinfo">
            <div className="sbn-role">{papel}</div>
            <div className="sbn-name">{usuario?.nome?.split(' ')[0] || 'Usuário'}</div>
          </div>
          {/* No celular este botão NÃO recolhe (12/08/2026, achado do Juan
              com print antes/depois do clique): o modo compacto é desfeito
              por inteiro dentro do @media de 640px desde 06/08 — a única
              coisa que mudava ao clicar era o padding das linhas, o que
              dava a impressão de botão quebrado. Como o botão de fechar
              próprio foi removido em 31/07 justamente por sobrepor este,
              no celular ele passa a FECHAR a gaveta: a seta já aponta pra
              esquerda, que é a direção pra onde o painel sai. No desktop
              continua recolhendo, sem mudança. */}
          <button className="sbn-collapse"
            onClick={() => { if (window.matchMedia('(max-width: 640px)').matches) onClose?.(); else alterarSidebarCompacta(!sidebarCompacta); }}
            title={sidebarCompacta ? 'Expandir' : 'Recolher'}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15,4.5 7.5,12 15,19.5" />
            </svg>
          </button>
        </div>

        <div className="sbn-search-wrap">
          <div className="sbn-search">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.85" strokeLinecap="round"><circle cx="10.5" cy="10.5" r="7.5" /><line x1="16.5" y1="16.5" x2="21.75" y2="21.75" /></svg>
            <input type="text" placeholder="Buscar sala..." value={busca} onChange={e => setBusca(e.target.value)} autoComplete="off" />
          </div>
        </div>

        <nav className="sbn-nav">
          <div
            className={`sbn-row ${location.pathname === '/dashboard' ? 'ativa' : ''}`}
            onClick={() => { navigate('/dashboard'); onClose?.(); }}
          >
            <span className="sbn-ico">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 11.25L12 3l9 8.25" /><path d="M5.25 9.75V19.5a1.5 1.5 0 001.5 1.5h10.5a1.5 1.5 0 001.5-1.5V9.75" />
              </svg>
            </span>
            <span className="sbn-row-nome">Home</span>
          </div>

          {listaCats.map(cat => (
            <React.Fragment key={cat.nome}>
              <div className={`sbn-cat${cat.fixa ? ' sbn-cat-fixa' : ''}`}>{cat.nome}</div>
              {cat.salas.map(s => {
                const ativa = location.pathname === `/sala/${s.sala_numero}`;
                return (
                  <div
                    key={`${cat.nome}-${s.sala_numero}`}
                    className={`sbn-row ${ativa ? 'ativa' : ''}`}
                    tabIndex={0}
                    role="button"
                    title={s.nome}
                    onClick={() => irPara(s.sala_numero)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); irPara(s.sala_numero); } }}
                    onMouseEnter={e => mostrarFlyout(s, e.currentTarget)}
                    onMouseLeave={agendarEsconderFlyout}
                  >
                    <span className="sbn-ico">
                      {/* ⚠ 19px, E O TAMANHO FOI MEDIDO, NÃO ESCOLHIDO
                          (08/09/2026, pedido do dono do produto: "pode
                          aumentar o tamanho dos ícones na sidebar").

                          Rasterizei os 43 ícones em sete tamanhos e medi o
                          BORRÃO — quanto do traço vira meio-tom em vez de
                          traço. O resultado NÃO é uma curva suave:

                            15px  55,0% de borrão  (era o que estava aqui)
                            16px  64,0%   ⚠ PIOR que 15
                            17px  55,8%
                            18px  46,8%
                            19px  42,3%   ← o melhor dos sete
                            20px  45,4%
                            22px  53,1%

                          Aumentar "um pouco" para 16px deixaria os ícones
                          MAIS borrados que estavam. O `tracoIcone` acerta o
                          alvo de ~1px renderizado em todos eles — o que
                          varia é onde a GEOMETRIA de cada desenho cai na
                          grade de pixels, e isso não acompanha o tamanho de
                          forma linear.

                          A caixa de 30px do `.sbn-ico` não muda: 19 caberia
                          até 26. */}
                      <SalaIcon numero={s.sala_numero} size={19} />
                    </span>
                    <span className="sbn-row-nome">{s.nome}</span>
                    {s.badge ? <span className={`sbn-pend ${s.badgeTipo || ''}`}>{s.badge}</span> : null}
                    <button type="button" className={`sbn-fixar${fixadas.includes(s.sala_numero) ? ' on' : ''}`}
                      aria-label={fixadas.includes(s.sala_numero) ? `Tirar ${s.nome} de Minhas salas` : `Fixar ${s.nome} em Minhas salas`}
                      title={fixadas.includes(s.sala_numero) ? 'Tirar de Minhas salas' : 'Fixar em Minhas salas'}
                      onClick={e => alternarFixada(e, s.sala_numero)} onKeyDown={e => e.stopPropagation()}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill={fixadas.includes(s.sala_numero) ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.85" strokeLinejoin="round" aria-hidden="true"><path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 16.9l-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z" /></svg>
                    </button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </nav>
      </aside>

      {/* Fora do <aside> de propósito (04/08/2026, achado real) — o
          <aside> tem backdrop-filter (vidro fosco), e por especificação
          do CSS (mesma regra de filter/transform) isso faz ele se tornar
          o "containing block" de qualquer descendente position:fixed.
          Só que o overlay do modal (.modal-overlay) É fixed — com o
          modal dentro do <aside>, o overlay ficava presa na caixa
          estreita da sidebar (252px) em vez de cobrir a tela toda, e
          sobrava zero espaço "fora do cartão" pra clicar — só o X
          fechava. Como irmão do <aside> (mesmo nível do sbn-flyout
          abaixo, que tem o mesmo motivo de ficar fora), o overlay cobre
          a tela de verdade e o clique fora passa a fechar, igual no
          celular e no computador. */}
      <MeuPerfilModal aberto={modalPerfil} onClose={() => setModalPerfil(false)} />

      {flyout && (
        <div
          className="sbn-flyout"
          style={{ top: flyout.top, left: flyout.left }}
          onMouseEnter={() => clearTimeout(esconderTimeout.current)}
          onMouseLeave={agendarEsconderFlyout}
        >
          <div className="sbn-flyout-titulo">{flyout.nome}</div>
          {flyout.abas.map(a => (
            <div key={a.slug} className="sbn-flyout-item" onClick={() => irPara(flyout.numero, a.slug)}>
              {a.label}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// Nota: "compacta" persiste de verdade agora (usuarios.sidebar_compacta,
// migration 285) — pendência da entrega anterior, fechada nesta rodada.
// Mesmo padrão de tema/nav_estilo: local primeiro (feedback imediato),
// depois salva no servidor via alterarSidebarCompacta (AuthContext).
